import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import api from "../lib/api";
import NewsCard from "../components/NewsCard";
import Reveal from "../components/Reveal";
import { useLang, SECTIONS } from "../lib/i18n";

export default function SearchPage() {
  const { lang, t } = useLang();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const cat = params.get("cat") || "";
  const [input, setInput] = useState(q);
  const [articles, setArticles] = useState(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setInput(q);
    if (!q) {
      setArticles([]);
      return;
    }
    setArticles(null);
    let url = `/articles?q=${encodeURIComponent(q)}&limit=40`;
    if (cat) url += `&category=${cat}`;
    api
      .get(url)
      .then((r) => {
        setArticles(r.data.articles);
        setTotal(r.data.total);
      })
      .catch(() => setArticles([]));
  }, [q, cat]);

  const submit = (e) => {
    e.preventDefault();
    if (input.trim()) setParams({ q: input.trim(), ...(cat ? { cat } : {}) });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-10" data-testid="search-page">
      <header className="mb-8">
        <h1 className="font-hindi text-3xl md:text-5xl text-ink">{t.searchTitle}</h1>
        <span className="block gold-rule w-32 mt-4" />
      </header>

      <form onSubmit={submit} data-testid="search-form" className="max-w-2xl">
        <div className="flex items-center border-2 border-ink bg-white focus-within:border-deepgreen transition-colors">
          <input
            data-testid="search-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t.searchPlaceholder}
            className="w-full bg-transparent px-4 py-3 outline-none text-base placeholder:text-muted-foreground"
          />
          <button
            data-testid="search-submit"
            type="submit"
            className="shrink-0 bg-deepgreen text-paper px-5 py-3 hover:bg-deepgreen-hover transition-colors flex items-center gap-2 font-semibold"
          >
            <Search size={16} />
            <span className="hidden sm:inline">{lang === "hi" ? "खोजें" : "Search"}</span>
          </button>
        </div>
      </form>

      <div className="flex flex-wrap gap-2 mt-6" data-testid="search-filters">
        <button
          data-testid="filter-all"
          onClick={() => setParams({ ...(q ? { q } : {}) })}
          className={`px-3.5 py-1.5 text-xs font-mono uppercase tracking-widest border transition-colors ${
            !cat ? "bg-ink text-paper border-ink" : "border-ink/25 text-ink hover:border-deepgreen"
          }`}
        >
          {t.all}
        </button>
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            data-testid={`filter-${s.id}`}
            onClick={() => setParams({ ...(q ? { q } : {}), cat: s.id })}
            className={`px-3.5 py-1.5 text-xs font-mono uppercase tracking-widest border transition-colors ${
              cat === s.id ? "bg-ink text-paper border-ink" : "border-ink/25 text-ink hover:border-deepgreen"
            }`}
          >
            {lang === "hi" ? s.hi : s.en}
          </button>
        ))}
      </div>

      <div className="mt-10">
        {!q ? (
          <p className="text-muted-foreground text-sm">{t.searchPlaceholder}</p>
        ) : !articles ? (
          <p className="font-mono text-sm text-muted-foreground">…</p>
        ) : articles.length === 0 ? (
          <p data-testid="search-empty" className="py-16 text-center text-muted-foreground">
            {t.noResults}
          </p>
        ) : (
          <>
            <p className="font-mono text-xs text-muted-foreground mb-6" data-testid="search-count">
              {total} {t.stories} — “{q}”
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
              {articles.map((a, i) => (
                <Reveal key={a.id} delay={Math.min(i * 0.05, 0.4)}>
                  <NewsCard article={a} variant="standard" />
                </Reveal>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
